/**
 * Campanhas recorrentes do Master — regras de TELA (pedido de 19/10, itens 5, 10 e 11 — Fase 5).
 *
 * Contrato com o backend (nomes exatos):
 * - Segmentos novos: "oleo_premio_ativo" (prêmio de troca de óleo disponível que vence em até
 *   dias_segmento dias, padrão 7; 1 envio por prêmio; unidade = onde o prêmio foi ganho),
 *   "indicacao_ativos" (códigos ativos não usados que vencem em até dias_segmento dias, padrão 5;
 *   1 aviso por código), "indicacao_expirados" (códigos vencidos sem uso nos últimos dias_segmento
 *   dias, padrão 30; 1 aviso por código) e "rota_fidelizacao" (clientes na Rota; filtro opcional
 *   rota_etapa: "1"|"2"|"3"|"concluido"|"premio").
 * - Variáveis novas: {placa} {vencimento} {percentual} {dias_restantes} {qtd_codigos} {codigos}
 *   {qtd_expirados} {etapa} {proximo_desconto} {premio}, validadas como as atuais.
 * - categorias_alvo (lista "normal" | "app_driver"; o backend já aceitava, a tela não mostrava).
 * - Preview: total que REALMENTE seria enviado (categoria, unidade, opt-in e dedupe), total_segmento
 *   (depois dos filtros, antes do corte por execução), cortado, limite, excluidos {motivo: n},
 *   avisos [...] (indicação/Rota desligada, ambiente de teste, WhatsApp oficial) e amostra
 *   [{nome, telefone, mensagem, mensagem_sms (só no canal "ambos")}].
 * - Envios: GET /master/campanhas/{id}/envios e /master/campanhas/envios ?unit_id&categoria&inicio
 *   &fim&sucesso&page (+ /excel).
 */
import { inicioDiaBR, fimDiaBR } from "@/lib/periodoBR";
import { smsAscii, SMS_LIMITE, PROVEDOR_WHATSAPP_OFICIAL } from "@/lib/mensagensAuto";

export const SEGMENTOS = {
  sumidos: "Clientes Sumidos",
  primeiro_nao_voltou: "1º Cadastro sem retorno",
  frequentes: "Frequentes (alto)",
  low_freq: "Baixa Frequência",
  saudade_enviado: "Já receberam saudade",
  todos: "Todos os clientes",
  oleo_premio_ativo: "Prêmio de troca de óleo ativo",
  indicacao_ativos: "Códigos de indicação ativos",
  indicacao_expirados: "Códigos de indicação expirados",
  rota_fidelizacao: "Rota de Fidelização",
};

/** Segmentos novos (Fase 5): o público é por prêmio/código/etapa, não por frequência. */
export const SEGMENTOS_NOVOS = ["oleo_premio_ativo", "indicacao_ativos", "indicacao_expirados", "rota_fidelizacao"];

/** Etapas da Rota no filtro da campanha ("" = todos os clientes na Rota). */
export const ROTA_ETAPAS_CAMPANHA = [
  ["", "Todos na Rota"],
  ["1", "Etapa 1 (próximo: desconto da Etapa 1)"],
  ["2", "Etapa 2 (próximo: desconto da Etapa 2)"],
  ["3", "Etapa 3 (próximo: desconto da Etapa 3)"],
  ["concluido", "Concluíram a Rota"],
  ["premio", "Com prêmio disponível"],
];
const ROTA_ETAPA_CURTO = { 1: "Etapa 1", 2: "Etapa 2", 3: "Etapa 3", concluido: "Concluídos", premio: "Prêmio disponível" };

/** "Rota de Fidelização · Etapa 2" (rótulo do segmento na tabela). */
export function rotuloSegmento(c) {
  const base = SEGMENTOS[c?.segmento] || c?.segmento || "—";
  if (c?.segmento === "rota_fidelizacao" && c?.rota_etapa) {
    return `${base} · ${ROTA_ETAPA_CURTO[c.rota_etapa] || c.rota_etapa}`;
  }
  return base;
}

/**
 * Campo "dias" de cada segmento: rótulo, valor padrão e explicação. null = o segmento não usa.
 */
export const DIAS_SEGMENTO = {
  sumidos: { label: "Dias sem abastecer", padrao: 30, ajuda: "" },
  oleo_premio_ativo: {
    label: "Avisar quando faltarem até (dias) para vencer",
    padrao: 7,
    ajuda: "Cada prêmio recebe no máximo 1 aviso. O filtro de unidade usa a unidade onde o prêmio foi ganho.",
  },
  indicacao_ativos: {
    label: "Avisar quando faltarem até (dias) para o código vencer",
    padrao: 5,
    ajuda: "Cada código ativo e não usado recebe no máximo 1 aviso.",
  },
  indicacao_expirados: {
    label: "Códigos vencidos sem uso nos últimos (dias)",
    padrao: 30,
    ajuda: "Cada código vencido recebe no máximo 1 aviso.",
  },
};
export function campoDias(segmento) {
  return DIAS_SEGMENTO[segmento] || null;
}

/** Variáveis que valem em toda campanha. */
export const VARIAVEIS_BASE = ["nome", "desconto", "telefone", "unidade", "valor"];

/**
 * Variáveis preenchidas só no segmento indicado. CÓPIA EXATA (mesma ordem) do VARIAVEIS_SEGMENTO
 * do backend (services/campaign_scheduler.py): o teste lib/__tests__/campanhas.test.jsx compara
 * com toEqual. Mudou lá, muda aqui.
 */
export const VARIAVEIS_SEGMENTO = {
  oleo_premio_ativo: ["placa", "vencimento", "percentual", "dias_restantes"],
  indicacao_ativos: ["qtd_codigos", "codigos", "vencimento", "dias_restantes", "premio"],
  indicacao_expirados: ["qtd_expirados", "codigos", "premio"],
  rota_fidelizacao: ["etapa", "proximo_desconto", "premio"],
};

export const VARIAVEIS_DESCRICAO = {
  nome: "nome do cliente",
  desconto: "desconto do cupom saudade (centavos/L)",
  telefone: "telefone do cliente",
  unidade: "nome da unidade",
  valor: "saldo de cashback (R$)",
  placa: "placa do veículo",
  vencimento: "data de vencimento (dia/mês)",
  percentual: "percentual de desconto na troca de óleo",
  dias_restantes: "dias que faltam para vencer",
  qtd_codigos: "quantidade de códigos ativos",
  codigos: "lista dos códigos do segmento (ativos ou vencidos); 9 caracteres cada",
  qtd_expirados: "quantidade de códigos vencidos sem uso",
  etapa: "etapa da Rota do cliente",
  proximo_desconto: "desconto do próximo abastecimento na Rota",
  premio: "valor do prêmio (indicação: por código usado; Rota: ao concluir)",
};

/** Todas as variáveis aceitas pelo backend (base + novas). */
export const TODAS_VARIAVEIS = [...new Set([...VARIAVEIS_BASE, ...Object.values(VARIAVEIS_SEGMENTO).flat()])];

/** Variáveis mostradas para o segmento: base + as do segmento. */
export function variaveisDoSegmento(segmento) {
  return [...VARIAVEIS_BASE, ...(VARIAVEIS_SEGMENTO[segmento] || []).filter((v) => !VARIAVEIS_BASE.includes(v))];
}

/** Segmentos em que a variável é preenchida (para o aviso de variável fora do segmento). */
function segmentosDaVariavel(v) {
  return Object.entries(VARIAVEIS_SEGMENTO).filter(([, vs]) => vs.includes(v)).map(([s]) => s);
}

const VAR_RE = /\{([^{}]*)\}/g;

/**
 * Confere as variáveis do texto (o backend confere de novo ao gravar):
 * - erro: variável que não existe (bloqueia salvar, como o 422 do backend);
 * - aviso: variável de outro segmento (sai vazia neste).
 */
export function conferirVariaveis(texto, segmento) {
  const usadas = [...new Set([...String(texto ?? "").matchAll(VAR_RE)].map((m) => m[1]))];
  const desconhecidas = usadas.filter((v) => !TODAS_VARIAVEIS.includes(v)).sort();
  const doSegmento = variaveisDoSegmento(segmento);
  const fora = usadas.filter((v) => TODAS_VARIAVEIS.includes(v) && !doSegmento.includes(v));
  const erro = desconhecidas.length
    ? `Variável não suportada na mensagem: ${desconhecidas.map((v) => `{${v}}`).join(", ")}. Use apenas: ${doSegmento.map((v) => `{${v}}`).join(" ")}`
    : null;
  const aviso = fora.length
    ? `${fora.map((v) => `{${v}}`).join(", ")} só tem valor em: ${[...new Set(fora.flatMap(segmentosDaVariavel))].map((s) => SEGMENTOS[s]).join(", ")}. Neste segmento sai vazia.`
    : null;
  return { erro, aviso };
}

/** Texto padrão do formulário novo (segmentos antigos). */
export const TEMPLATE_PADRAO = "Ola {nome}! Volte ao posto e ganhe {desconto} centavos/L de desconto no proximo abastecimento.";

/**
 * Texto sugerido de cada segmento novo (sem acentos e sem emojis). Cabe num SMS mesmo com valores
 * reais longos (nome completo, 6 códigos de 9 caracteres): por isso o de códigos ativos usa
 * {qtd_codigos} e não a lista {codigos}.
 */
export const TEMPLATE_SUGERIDO = {
  oleo_premio_ativo: "Ola {nome}! Seu desconto de {percentual}% na troca de oleo do veiculo {placa} vence em {vencimento}. Aproveite no posto!",
  indicacao_ativos: "Ola {nome}! Voce tem {qtd_codigos} codigo(s) de indicacao sem uso que vencem em {vencimento}. Envie a amigos e ganhe a cada uso!",
  indicacao_expirados: "Ola {nome}! {qtd_expirados} codigo(s) de indicacao venceram sem uso. Passe no posto, gere novos codigos e nao deixe de ganhar!",
  rota_fidelizacao: "Ola {nome}! Continue na Rota de Fidelizacao: seu proximo abastecimento tem desconto especial. Esperamos voce!",
};
export function templateDoSegmento(segmento) {
  return TEMPLATE_SUGERIDO[segmento] || TEMPLATE_PADRAO;
}

/**
 * Troca de segmento no formulário: ajusta o padrão dos dias, troca o texto se ainda for o
 * sugerido do segmento anterior (não apaga o que o Master escreveu) e, nos segmentos novos,
 * desliga o cupom saudade (o público é por prêmio/código/etapa).
 */
export function trocarSegmento(f, novo) {
  const prox = { ...f, segmento: novo };
  if (novo === f.segmento) return prox;
  const dias = campoDias(novo);
  if (dias) prox.dias_segmento = dias.padrao;
  const textoAtual = String(f.mensagem_template ?? "").trim();
  if (!textoAtual || textoAtual === templateDoSegmento(f.segmento) || textoAtual === TEMPLATE_PADRAO) {
    prox.mensagem_template = templateDoSegmento(novo);
  }
  if (SEGMENTOS_NOVOS.includes(novo) && !SEGMENTOS_NOVOS.includes(f.segmento)) prox.aplicar_cupom_saudade = false;
  return prox;
}

/**
 * Valores típicos LONGOS para estimar o SMS final (o envio troca as variáveis e depois corta em
 * 160): nome completo, 6 códigos no formato real ("IND" + 6 caracteres) etc.
 */
export const EXEMPLO_TAMANHO = {
  nome: "Maria Aparecida dos Santos",
  desconto: "15",
  telefone: "(62) 99999-0000",
  unidade: "Posto Lacador Centro",
  valor: "125.50",
  placa: "ABC1D23",
  vencimento: "31/10",
  percentual: "50",
  dias_restantes: "5",
  qtd_codigos: "6",
  codigos: "IND4K7P2Q, IND8M3R5T, IND2B9C6D, IND7F1G4H, IND5J8K2L, IND3N6P9Q",
  qtd_expirados: "6",
  etapa: "2",
  proximo_desconto: "0,20",
  premio: "250,00",
};

/** Texto com as variáveis trocadas pelos valores de EXEMPLO_TAMANHO (desconhecida fica literal). */
export function renderExemploTamanho(texto) {
  return String(texto ?? "").replace(VAR_RE, (m, v) => (Object.prototype.hasOwnProperty.call(EXEMPLO_TAMANHO, v) ? EXEMPLO_TAMANHO[v] : m));
}

/**
 * Tamanho estimado do SMS com valores reais longos (EXEMPLO_TAMANHO). Devolve {caracteres, aviso}:
 * aviso quando o texto cabe sem as variáveis mas, com elas, passa de 160 e é cortado.
 */
export function estimativaSms(texto) {
  const t = String(texto ?? "");
  const caracteres = smsAscii(renderExemploTamanho(t)).length;
  const semVars = smsAscii(t).length;
  if (caracteres <= SMS_LIMITE || semVars > SMS_LIMITE) return { caracteres, aviso: null };
  const lista = t.includes("{codigos}");
  return {
    caracteres,
    aviso: `Com valores reais longos (nome completo${lista ? ", 6 códigos" : ""}) o SMS fica com cerca de ${caracteres} `
      + `caracteres e é cortado no ${SMS_LIMITE}º.${lista ? " Prefira {qtd_codigos} à lista {codigos} (9 caracteres por código)." : ""}`,
  };
}

/** Categorias de cliente (categorias_alvo). */
export const CATEGORIAS = [["normal", "Cliente Normal"], ["app_driver", "Motorista de App"]];
export const CATEGORIA_LBL = { normal: "Cliente Normal", app_driver: "Motorista de App" };

/** categorias_alvo salva -> lista para o formulário (vazio/ausente = as duas, como o backend). */
export function categoriasIniciais(c) {
  const lista = Array.isArray(c?.categorias_alvo) ? c.categorias_alvo.filter((x) => CATEGORIA_LBL[x]) : [];
  return lista.length ? lista : CATEGORIAS.map(([k]) => k);
}

/** "Todas" ou "Cliente Normal" (para a tabela). */
export function rotuloCategorias(lista) {
  const l = Array.isArray(lista) ? lista.filter((x) => CATEGORIA_LBL[x]) : [];
  if (!l.length || l.length === CATEGORIAS.length) return "Todas";
  return l.map((x) => CATEGORIA_LBL[x]).join(", ");
}

/** Corpo do POST/PUT: rota_etapa só vai no segmento da Rota ("" = null). */
export function payloadCampanha(f) {
  const body = { ...f };
  if (body.segmento === "rota_fidelizacao") body.rota_etapa = body.rota_etapa || null;
  else delete body.rota_etapa;
  return body;
}

/**
 * Resultado do "Executar agora": o backend pode responder com os números (execução na hora) ou
 * só confirmar que a execução começou em segundo plano. Devolve {titulo, mensagem, details}.
 */
export function resumoExecucao(data, nome) {
  const d = data || {};
  const numerico = ["enviados", "erros", "total"].some((k) => Number.isFinite(Number(d[k])) && d[k] !== null && d[k] !== undefined);
  const fundo = d.em_segundo_plano || d.background || d.em_andamento || d.status === "em_andamento" || d.status === "iniciada" || d.iniciada;
  if (fundo || !numerico) {
    return {
      titulo: "Campanha iniciada",
      mensagem: `"${nome}" está sendo enviada em segundo plano. Acompanhe o resultado no histórico de envios.`,
      details: d.total != null ? [{ label: "Clientes elegíveis", value: String(d.total) }] : [],
    };
  }
  return {
    titulo: "Campanha executada",
    mensagem: `"${nome}" foi disparada.`,
    details: [
      { label: "Enviados", value: String(d.enviados ?? 0) },
      { label: "Erros", value: String(d.erros ?? 0) },
      { label: "Total", value: String(d.total ?? 0) },
    ],
  };
}

/** Rótulos dos motivos de fora do envio no preview (o backend pode mandar outros). */
export const MOTIVOS_EXCLUSAO_LBL = {
  sem_optin: "Recusaram receber ofertas",
  optin_recusado: "Recusaram receber ofertas",
  ja_enviados: "Já avisados antes",
  ja_avisados: "Já avisados antes",
  dedupe: "Já avisados antes",
  sem_telefone: "Sem telefone válido",
  telefone_invalido: "Sem telefone válido",
  fora_categoria: "Fora das categorias",
  fora_unidade: "Fora da unidade",
  inativos: "Cadastro inativo",
  limite: "Acima do limite por execução",
  indicacao_desligada: "Indicação desligada",
  rota_desligada: "Rota desligada",
  rota_sem_valores: "Rota sem valores na unidade/categoria",
};

/** Lista [{chave, rotulo, n}] dos motivos com n > 0 (aceita excluidos | motivos | descartados). */
export function motivosExclusao(prev) {
  const obj = prev?.excluidos || prev?.motivos || prev?.descartados;
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return [];
  return Object.entries(obj)
    .map(([chave, n]) => ({ chave, rotulo: MOTIVOS_EXCLUSAO_LBL[chave] || chave.replace(/_/g, " "), n: Number(n) || 0 }))
    .filter((m) => m.n > 0);
}

// Avisos do preview (campaign_scheduler.py: AVISO_OFICIAL e AVISO_AMBIENTE_TESTE). O texto do
// backend é o que aparece; estes testes só reconhecem qual aviso é qual.
const RE_AVISO_OFICIAL = /API oficial/i;
const RE_AVISO_TESTE = /ambiente de teste/i;
// Pedido de 26/10: template padrão das campanhas aprovado e promoções liberadas no Master
// (AVISO_TEMPLATE_CAMPANHA do backend): o WhatsApp sai, com o texto padrão aprovado na Meta.
const RE_AVISO_TEMPLATE = /texto padrão aprovado/i;

/** Campanha só de WhatsApp com a API oficial em uso: nada sai (marketing sem aceite). */
export const AVISO_SO_WHATSAPP_OFICIAL = "WhatsApp em uso: API oficial da Meta. Campanha é promoção (marketing) e só sai "
  + "depois do aceite do cliente e da liberação. Esta campanha é só de WhatsApp: nenhuma mensagem é enviada enquanto "
  + "isso. Para alcançar os clientes agora, troque o canal para SMS ou SMS + WhatsApp.";
export const AVISO_AMBOS_OFICIAL = "WhatsApp em uso: API oficial da Meta. Campanha é promoção (marketing) e só sai depois "
  + "do aceite do cliente e da liberação; até lá, só o SMS desta campanha sai.";
export const AVISO_TESTE = "Ambiente de teste: só os telefones da lista de teste (allowlist) recebem; os outros envios "
  + "ficam registrados como bloqueados.";

const FORA = "Já sem quem fica de fora: categoria, unidade, recusa de ofertas e quem já foi avisado.";

/**
 * Leitura do preview para a tela. O total do backend é o público que receberia; o que REALMENTE
 * sai depende do canal e do WhatsApp em uso. `status` (opcional) = GET /master/whatsapp/oficial/
 * status; sem ele valem os avisos do próprio preview (o backend já resolve o provedor pelo canal).
 * Devolve {total, nadaSai, legenda, avisos[]}:
 * - só WhatsApp + API oficial: nadaSai; a legenda diz que nenhum envio sai e o aviso do backend
 *   ("só o SMS sai", que não vale sem SMS) dá lugar a um que diz o que fazer;
 * - SMS + WhatsApp + API oficial: a legenda diz que só o SMS sai;
 * - ambiente de teste: o aviso da allowlist (vem do backend; se faltar e o status disser, a tela põe).
 */
export function resumoPreview(prev, canal, status) {
  const p = prev || {};
  const total = Number(p.total) || 0;
  const doBackend = (Array.isArray(p.avisos) ? p.avisos : []).filter((a) => typeof a === "string" && a.trim());
  const liberadoOficial = doBackend.some((a) => RE_AVISO_TEMPLATE.test(a));
  const backendOficial = doBackend.some((a) => RE_AVISO_OFICIAL.test(a) && !RE_AVISO_TEMPLATE.test(a));
  const temWhats = canal === "whatsapp" || canal === "ambos";
  const oficial = temWhats && !liberadoOficial
    && (status?.provedor_ativo === PROVEDOR_WHATSAPP_OFICIAL || backendOficial);
  const nadaSai = oficial && canal === "whatsapp";
  const avisos = [];
  if (nadaSai) avisos.push(AVISO_SO_WHATSAPP_OFICIAL);
  else if (oficial && !backendOficial) avisos.push(AVISO_AMBOS_OFICIAL);
  doBackend.forEach((a) => {
    if (nadaSai && RE_AVISO_OFICIAL.test(a)) return;
    if (!avisos.includes(a)) avisos.push(a);
  });
  if (status?.ambiente === "teste" && !avisos.some((a) => RE_AVISO_TESTE.test(a))) avisos.push(AVISO_TESTE);
  let legenda = `envios seriam feitos agora. ${FORA}`;
  if (nadaSai) {
    legenda = `${total === 1 ? "cliente está" : "clientes estão"} no público, mas NENHUM envio sai agora: a campanha é só `
      + "de WhatsApp e a API oficial da Meta não envia campanha sem o aceite do cliente.";
  } else if (oficial) {
    legenda = `${total === 1 ? "cliente receberia" : "clientes receberiam"} o SMS agora; o WhatsApp desta campanha não sai `
      + `pela API oficial sem o aceite do cliente. ${FORA}`;
  }
  return { total, nadaSai, legenda, avisos };
}

/**
 * Texto do corte por execução. total_segmento é o público DEPOIS dos filtros e ANTES do corte do
 * limite por execução. null quando não houve corte.
 */
export function textoCorte(prev) {
  if (!prev?.cortado) return null;
  const lim = Number(prev.limite) || 0;
  const seg = prev.total_segmento == null ? NaN : Number(prev.total_segmento);
  if (lim && Number.isFinite(seg)) {
    return `${seg} ${seg === 1 ? "cliente" : "clientes"} no segmento; cada execução alcança só os ${lim} primeiros.`;
  }
  if (lim) return `O segmento passa do limite: cada execução alcança só os ${lim} primeiros.`;
  return "O segmento passa do limite por execução: nem todos recebem de uma vez.";
}

/** Canal de um envio. */
export const CANAL_ENVIO_LBL = { sms: "SMS", whatsapp: "WhatsApp", ambos: "SMS + WhatsApp" };

/** URL base dos envios (de uma campanha ou de todas). */
export function urlEnvios(campanhaId) {
  return campanhaId ? `/master/campanhas/${encodeURIComponent(campanhaId)}/envios` : "/master/campanhas/envios";
}

/**
 * Query string dos filtros dos envios: unidade, categoria, período (dia de Brasília: início 00:00 e
 * fim 23:59:59 em UTC, como as outras telas) e resultado. "all"/vazio não vai na URL.
 */
export function qsEnvios({ unitId, categoria, inicio, fim, sucesso } = {}) {
  const p = new URLSearchParams();
  if (unitId && unitId !== "all") p.set("unit_id", unitId);
  if (categoria && categoria !== "all") p.set("categoria", categoria);
  const ini = inicio ? inicioDiaBR(inicio) : null;
  const fi = fim ? fimDiaBR(fim) : null;
  if (ini) p.set("inicio", ini);
  if (fi) p.set("fim", fi);
  if (sucesso === "true" || sucesso === "false") p.set("sucesso", sucesso);
  return p.toString();
}

/** Totais do período (aceita totais:{sucesso, falha} ou total_sucesso/total_falha). */
export function totaisEnvios(data) {
  const t = data?.totais && typeof data.totais === "object" ? data.totais : {};
  const sucesso = t.sucesso ?? t.enviados ?? data?.total_sucesso;
  const falha = t.falha ?? t.falhas ?? t.erros ?? data?.total_falha;
  if (sucesso == null && falha == null) return null;
  return { sucesso: Number(sucesso) || 0, falha: Number(falha) || 0 };
}
